import classNames from 'classnames';
import PropTypes from 'prop-types';
import * as React from 'react';
import {Table} from 'reactstrap';
import * as ColumnAlignment from './ColumnAlignment';
import type ColumnSynchronizer from './ColumnSynchronizer';
import * as ColumnType from './ColumnType';
import './DataTable.scss';
import DataTableBody from './DataTableBody';
import DataTableHeader from './DataTableHeader';
import type {ColumnPropType, RowIdPropType, RowPropType} from './propTypes';
import {columnPropType, columnSynchronizerPropType, rowIdPropType, rowPropType} from './propTypes';

export type Props = {
    /** Definice columns */
    columns: ColumnPropType,
    /** Synchronizace sloupců oddělené hlavičky a těla tabulky  */
    columnSynchronizer?: ColumnSynchronizer,
    /** seznam viditelných položek - nepovinný */
    visibleColumnNames?: string[],
    /** Data - nutno mít v objektu ID - které bude number nebo string */
    rows: RowPropType[],
    /** Vybrená ID */
    selectedRowIds?: RowIdPropType,
    /** Tabulka s fixním layoutem */
    fixed?: boolean,
    /** Stripovaná tabulka */
    striped?: boolean,
    /** Na najetí zvýrazní řádek */
    hover?: boolean,
    /** Border kolem babulky */
    bordered?: boolean,
    /** Renderovat pouze hlavičku */
    headerOnly?: boolean,
    /** Props hlavičky */
    headerProps?: Object,
    /** Props řádky hlavičky */
    headerRowProps?: Object,
    /** Renderovat pouze tělo */
    bodyOnly?: boolean,
    /** Props body */
    bodyProps?: Object,
    /** Props řádky body */
    bodyRowProps?: Object,
    /** Klik na řádek */
    onRowClick?: (row: RowPropType) => void,
    /** Změna výberu */
    onRowSelectionChange?: (row: RowPropType, value: boolean, column: ColumnPropType) => void,
    /** Vybrat vše */
    onAllRowsSelectionChange?: (value: boolean, column: ColumnPropType) => void,
}

export default class DataTable extends React.PureComponent<Props> {

    static ColumnAlignment = ColumnAlignment;
    static ColumnType = ColumnType;

    static propTypes = {
        columns: PropTypes.arrayOf(columnPropType).isRequired,
        columnSynchronizer: columnSynchronizerPropType,
        visibleColumnNames: PropTypes.arrayOf(PropTypes.string),
        rows: PropTypes.arrayOf(rowPropType).isRequired,
        selectedRowIds: PropTypes.arrayOf(rowIdPropType),
        fixed: PropTypes.bool,
        striped: PropTypes.bool,
        hover: PropTypes.bool,
        bordered: PropTypes.bool,
        headerOnly: PropTypes.bool,
        headerProps: PropTypes.object,
        headerRowProps: PropTypes.object,
        bodyOnly: PropTypes.bool,
        bodyProps: PropTypes.object,
        bodyRowProps: PropTypes.object,
        onRowClick: PropTypes.func,
        onRowSelectionChange: PropTypes.func,
        onAllRowsSelectionChange: PropTypes.func,
    };

    static defaultProps = {
        selectedRowIds: [],
        striped: false,
        hover: false,
        bordered: false,
        headerOnly: false,
        bodyOnly: false,
    };

    static contextTypes = {
        columnSynchronizer: columnSynchronizerPropType,
    };

    props: Props;

    getColumn = (name: string): ColumnType => {
        const {columns} = this.props;
        for (let i = 0; i < columns.length; i++) {
            if (columns[i].name === name) {
                return columns[i];
            }
        }
        throw new Error(`Unknown table column '${name}'`);
    };

    render(): React.ReactNode {
        const {
            style: baseStyle,
            className: baseClassName,
            columns,
            columnSynchronizer: baseColumnSynchronizer,
            visibleColumnNames,
            rows,
            selectedRowIds,
            fixed,
            striped,
            hover,
            bordered,
            headerOnly,
            headerProps,
            headerRowProps,
            bodyOnly,
            bodyProps,
            bodyRowProps,
            onRowClick,
            onRowSelectionChange,
            onAllRowsSelectionChange,
            ...otherProps
        } = this.props;

        const className = classNames('data-table', baseClassName);
        const visibleColumns = visibleColumnNames ? visibleColumnNames.map(this.getColumn) : columns;
        const headerSelected = selectedRowIds.length > 0 && selectedRowIds.length === rows.length;

        let columnSynchronizer = baseColumnSynchronizer;
        if (!columnSynchronizer && this.context) {
            columnSynchronizer = this.context.columnSynchronizer;
        }

        // Synchronizace sloupců samostatné hlavičky vyžaduje fixed layout.
        // Zcela záměrně kontrolujeme i počet řádků. Při prázdné tabulce,
        // nevíme ještě velikosti sloupců a hlavička s fixed layoutem se
        // pak v některých případech renderuje "divně".

        let style = baseStyle;
        if (fixed || rows.length && headerOnly && columnSynchronizer) {
            const tableLayout = 'fixed';
            style = style ? {tableLayout, ...style} : {tableLayout};
        }

        return (
            <Table className={className}
                   striped={striped}
                   hover={hover}
                   bordered={bordered}
                   style={style}
                   {...otherProps}>

                {!bodyOnly && (
                    <DataTableHeader {...headerProps}
                                     columns={visibleColumns}
                                     columnSynchronizer={columnSynchronizer}
                        /* Padding kvůli možnému scrollbaru v odděleném těle tabulky. */
                                     paddingColumn={headerOnly && columnSynchronizer != null}
                                     selected={headerSelected}
                                     rowProps={headerRowProps}
                                     onSelectionChange={onAllRowsSelectionChange}/>
                )}

                {!headerOnly && (
                    <DataTableBody {...bodyProps}
                                   columns={visibleColumns}
                                   columnSynchronizer={columnSynchronizer}
                                   rows={rows}
                                   selectedRowIds={selectedRowIds}
                                   rowProps={bodyRowProps}
                                   onRowClick={onRowClick}
                                   onRowSelectionChange={onRowSelectionChange}/>
                )}
            </Table>
        );
    }
}
